import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext'; // Import AuthContext
import './SettingsPage.css'; // Import styling for the Settings page

const SettingsPage = () => {
    const { t } = useTranslation(['settings', 'common']);
    const { updateSettings } = useContext(AuthContext); // Get updateSettings function from AuthContext
    const [settings, setSettings] = useState({
        notificationEmails: true,
        darkMode: false,
    });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);

    /**
     * Toggles a settings value.
     *
     * This page keeps the original lightweight settings model. The i18n change
     * is intentionally presentation-only: setting keys such as notificationEmails
     * and darkMode remain stable because they are part of the existing frontend ↔
     * AuthContext/backend contract.
     */
    const handleToggle = (key) => {
        setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
        setError('');
        setSuccessMessage('');
    };

    /**
     * Saves settings to the backend.
     */
    const handleSaveSettings = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const responseMessage = await updateSettings(settings);
            // Backend may return an explicit message. Keep it when available and
            // translate only the frontend-owned fallback message.
            setSuccessMessage(responseMessage || t('settings:general.messages.saved'));
        } catch (err) {
            console.error('[SettingsPage] Error saving settings:', err);
            setError(t('settings:general.messages.saveFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings-page">
            <h1>{t('settings:general.title')}</h1>
            <div className="settings-option">
                <label>
                    <input
                        type="checkbox"
                        checked={settings.notificationEmails}
                        onChange={() => handleToggle('notificationEmails')}
                    />
                    {t('settings:general.fields.notificationEmails')}
                </label>
            </div>
            <div className="settings-option">
                <label>
                    <input
                        type="checkbox"
                        checked={settings.darkMode}
                        onChange={() => handleToggle('darkMode')}
                    />
                    {t('settings:general.fields.darkMode')}
                </label>
            </div>
            <button
                onClick={handleSaveSettings}
                className="save-settings-button"
                disabled={loading}
            >
                {loading ? t('settings:general.actions.saving') : t('settings:general.actions.save')}
            </button>
            {error && <p className="error-message">{error}</p>}
            {successMessage && <p className="success-message">{successMessage}</p>}
        </div>
    );
};

export default SettingsPage;
